import { useEffect, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import type { NavSessionUser } from "~/lib/nav-session";
import { cn } from "~/lib/cn";
import { AppLink } from "~/components/ui/AppLink";
import { Button } from "~/components/ui";
import { Avatar } from "~/components/layout/Avatar";
import { LanguageSwitcher } from "~/components/ui/LanguageSwitcher";
import { useSignOut } from "~/components/layout/use-sign-out";
import {
  IconArrowRight,
  IconBell,
  IconChevron,
  IconGlobe,
  IconHelp,
  IconHome,
  IconUser,
  IconLogout,
} from "~/components/layout/nav-icons";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable], [tabindex]:not([tabindex="-1"])';

interface AvatarMenuProps {
  sessionUser: NavSessionUser;
  /**
   * T872 S1 — mounted in the public header (R-T872-3): the trigger reads «Mi espacio»
   * and the panel opens with «Ir a mi espacio», then Mi perfil, language, «Ayuda y
   * soporte» and sign out. Off (the member header) nothing changes.
   */
  publicContext?: boolean;
}

/**
 * Member account menu — T98 `.um-cluster`. Reserved (inert) bell + avatar trigger.
 * Desktop (≥48em — 768px at the default text size, T895): dropdown anchored under the trigger (`.um-pop`, absolute in the
 * cluster). Mobile (<48em): bottom sheet (`.um-sheet`) — portaled to <body> because
 * the header's `backdrop-filter` would otherwise capture `position: fixed`. A public
 * route has no sheet: below 48em its account actions are the cúter's (T872 N4).
 * Crossing 48em closes the menu, as it closes the cúter.
 * Idle content: identity · Mi perfil · Cerrar sesión (richer states land later).
 */
export function AvatarMenu({ sessionUser, publicContext = false }: AvatarMenuProps) {
  const { t } = useTranslation("nav");
  const [open, setOpen] = useState(false);
  // T872 S1 — the sign-out gesture lives in `useSignOut` (shared with the public cúter);
  // a resolved HTTP error keeps the menu open with the retry, like a rejection.
  const { signOut: handleSignOut, signingOut, signOutError } = useSignOut(() => setOpen(false));
  const clusterRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  const trimmedName = sessionUser.name.trim();
  const firstName = trimmedName.split(/\s+/)[0] || sessionUser.name;
  // The root loader sets name to "—" when Better Auth has no name → empty state.
  const profileIncomplete = trimmedName === "" || trimmedName === "—";
  const triggerLabel = publicContext
    ? t("links.dashboard")
    : profileIncomplete
      ? t("user_menu.title")
      : firstName;

  useEffect(() => {
    if (!open) return;
    // Move focus into the modal sheet when it is the visible surface (mobile).
    // On desktop the sheet is display:none, so focus() no-ops and focus stays put.
    sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (clusterRef.current?.contains(target) || sheetRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      // Trap Tab inside the modal sheet — engages only while focus is in it (mobile).
      if (e.key !== "Tab") return;
      const sheet = sheetRef.current;
      if (!sheet || !sheet.contains(document.activeElement)) return;
      const f = sheet.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (f.length === 0) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // The surface is chosen by the breakpoint, so an open menu does not survive crossing it
  // (T872 N4, Astra's second audit: open at 1280 → 390 brought the old sheet back over a
  // blurred page; open at 320 → 844 lost the focus to BODY). If the focus was in the menu —
  // or already lost with it — it lands on a control she can see: the trigger, or on a public
  // phone, where the bar's account group stands down, the menu button.
  useEffect(() => {
    if (!open || typeof window.matchMedia !== "function") return;
    const desktop = window.matchMedia("(min-width: 48em)");
    const closeOnCross = () => {
      const active = document.activeElement;
      const hadFocus =
        active == null ||
        active === document.body ||
        clusterRef.current?.contains(active) ||
        sheetRef.current?.contains(active);
      flushSync(() => setOpen(false));
      if (!hadFocus) return;
      const visible =
        publicContext && !desktop.matches
          ? clusterRef.current?.closest("header")?.querySelector<HTMLElement>("[data-menu-toggle]")
          : triggerRef.current;
      visible?.focus();
    };
    desktop.addEventListener("change", closeOnCross);
    return () => desktop.removeEventListener("change", closeOnCross);
  }, [open, publicContext]);

  // Sign-out stays accessible through the error state — the retry IS the action.
  const signOutSection = (
    <>
      {signOutError ? (
        <div className="um-error" role="alert">
          <span className="hd">{t("user_menu.signout_error_title")}</span>
          <Button
            type="button"
            variant="dark"
            className="self-start"
            onClick={handleSignOut}
            isLoading={signingOut}
          >
            {t("user_menu.error_retry")}
          </Button>
        </div>
      ) : null}
      {/* The delivered menu row (`usermenu.jsx`): left-aligned icon + label in signature ink,
          danger hover — a structural row, not a text link. T972 keeps its wait (ring + status). */}
      <Button
        type="button"
        variant="unstyled"
        className="um-item danger"
        isLoading={signingOut && !signOutError}
        busyLabel={t("user_menu.signing_out")}
        onClick={handleSignOut}
        disabled={signingOut}
      >
        <IconLogout className="ic" />
        <span className="lbl">
          {t("user_menu.log_out")}
        </span>
      </Button>
    </>
  );

  // EMPTY — session valid but the profile has no name yet.
  const menuContent = profileIncomplete ? (
    <>
      <div className="um-identity">
        <Avatar name="?" size="lg" />
        <span className="meta">
          <span className="nm">{t("user_menu.empty_name")}</span>
          {/* Real email — the incomplete signal is the name + the prompt below,
              not a fake value in the email slot (avoids "did I lose data?"). */}
          <span className="em">{sessionUser.email}</span>
        </span>
      </div>
      <hr className="um-divider" />
      <div className="um-empty">
        <span className="ttl">{t("user_menu.empty_title")}</span>
        <p>{t("user_menu.empty_body")}</p>
        <AppLink routeKey="profile" className="go" onClick={() => setOpen(false)}>
          {t("user_menu.empty_cta")}
          <IconArrowRight size={15} />
        </AppLink>
      </div>
      <hr className="um-divider" />
      <div role="none">{signOutSection}</div>
    </>
  ) : (
    <>
      <div className="um-identity">
        <Avatar name={sessionUser.name} size="lg" ring />
        <span className="meta">
          <span className="nm">{sessionUser.name}</span>
          <span className="em">{sessionUser.email}</span>
        </span>
      </div>
      <hr className="um-divider" />
      <div role="none">
        {publicContext ? (
          <AppLink routeKey="dashboard" className="um-item" onClick={() => setOpen(false)}>
            <IconHome className="ic" />
            <span className="lbl">{t("user_menu.go_dashboard")}</span>
          </AppLink>
        ) : null}
        <AppLink
          routeKey="profile"
          className="um-item"
          onClick={() => setOpen(false)}
        >
          <IconUser className="ic" />
          <span className="lbl">{publicContext ? t("public.my_profile") : t("user_menu.profile")}</span>
        </AppLink>
        {/* T393 — a live control, not a link to one. On a member surface the footer used
            to be the only place a language could be changed, and below 768px the footer
            now stands down; this puts it two taps from anywhere. The menu does not close
            on switching, because `LanguageSwitcher` navigates to the swapped path and
            closing first would fight that navigation. */}
        {/* `.lbl` already carries `flex: 1 1 auto`, so the switcher lands at the trailing
            edge without a layout override — the same place the delivered sheet puts it. */}
        {/* On a public route the bar (desktop) and the cúter (phone) already carry «ES / EN»,
            so the panel does not repeat it; on a private route this is her language control,
            in the same text form (navigation brief, 2026-10-10 § C). */}
        {publicContext ? null : (
          <div className="um-item is-static">
            <IconGlobe className="ic" />
            <span className="lbl">{t("user_menu.language")}</span>
            <LanguageSwitcher variant="text" />
          </div>
        )}
        {publicContext ? (
          <AppLink routeKey="faq" className="um-item" onClick={() => setOpen(false)}>
            <IconHelp className="ic" />
            <span className="lbl">{t("user_menu.help")}</span>
          </AppLink>
        ) : null}
        <hr className="um-divider" />
        {signOutSection}
      </div>
    </>
  );

  // The disclosure closes when keyboard focus leaves the trigger and its panel; the focus
  // stays where she moved it (only Escape returns it to the trigger). A pointer press
  // outside is the `mousedown` handler's; a move into the portaled sheet stays open.
  const closeOnFocusOut = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!open || !next) return;
    if (clusterRef.current?.contains(next) || sheetRef.current?.contains(next)) return;
    setOpen(false);
  };

  return (
    <div className="um-cluster" ref={clusterRef} onBlur={closeOnFocusOut}>
      <span className="um-bell" aria-hidden="true" title={t("user_menu.notifications")}>
        <IconBell />
      </span>

      {/* `unstyled`: the `text` variant's mono capitals made «Mi espacio» read «MI ESPACIO»;
          the trigger's own rule draws it (navigation brief, 2026-10-10 § B). */}
      <Button
        ref={triggerRef}
        type="button"
        variant="unstyled"
        className="um-trigger"
        aria-expanded={open}
        aria-label={t("user_menu.trigger")}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar name={profileIncomplete ? "?" : sessionUser.name} size="sm" ring />
        <span className="nm">{triggerLabel}</span>
        <IconChevron className="chev" />
      </Button>

      {/* Desktop: anchored dropdown (non-modal disclosure) stays inside the
          (relative) cluster. Items are natively focusable links/buttons. */}
      {open ? (
        // Non-modal disclosure: native <a>/<button> children + aria-expanded on the
        // trigger are sufficient (WCAG 4.1.2). No container role / aria-haspopup —
        // the surface varies by viewport (group on desktop, dialog sheet on mobile),
        // so no single value is accurate; aria-expanded carries the disclosure.
        <div className="um-pop um-pop--anchored" aria-busy={signingOut || undefined}>
          {menuContent}
        </div>
      ) : null}

      {/* Mobile: modal sheet + scrim portaled to <body> to escape the header's
          backdrop-filter containing block. Focus-trapped (see effect). Member header only. */}
      {open && !publicContext
        ? createPortal(
            <div ref={sheetRef}>
              <div className="um-scrim" aria-hidden="true" onClick={() => setOpen(false)} />
              <div
                className="um-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={t("user_menu.title")}
                aria-busy={signingOut || undefined}
              >
                <span className="grab" aria-hidden="true" />
                {menuContent}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
